import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { usePageTitle } from '@/shared/context/PageTitleContext';
import { radius } from '@/shared/theme/tokens';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@/shared/ui';

export interface FormPageProps {
  title: string;
  /** Rendered beside the back control (e.g. patient name + plan switcher). */
  titleExtra?: React.ReactNode;
  backTo?: string;
  onBack?: () => void;
  /** Omit to hide the primary submit button (view-only FormPage). */
  onSubmit?: () => void;
  submitLabel?: string;
  submitting?: boolean;
  /** Extra disable conditions beyond `submitting` (e.g. form incomplete). */
  submitDisabled?: boolean;
  error?: string | null;
  maxWidth?: 'md' | 'lg';
  footerExtra?: React.ReactNode;
  contentPaper?: boolean;
  /** Body fills remaining height so children can split into full-height panes. */
  contentFill?: boolean;
  children: React.ReactNode;
}

const FormPage: React.FC<FormPageProps> = ({
  title,
  titleExtra,
  backTo,
  onBack,
  onSubmit,
  submitLabel = 'Lưu',
  submitting = false,
  submitDisabled = false,
  error,
  maxWidth = 'md',
  footerExtra,
  contentPaper = true,
  contentFill = false,
  children,
}) => {
  const navigate = useNavigate();
  usePageTitle(title);

  const handleBack = (): void => {
    if (onBack) {
      onBack();
      return;
    }
    if (backTo) {
      navigate(backTo);
      return;
    }
    navigate(-1);
  };

  // Same band as ListPage: one Container, mt:2 from AppBar, mb:1 before body; toolbar stays put.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Container
        maxWidth={contentFill ? false : maxWidth}
        disableGutters={contentFill}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          px: contentFill ? 0 : undefined,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            mt: 2,
            mb: 1,
            flexShrink: 0,
            px: contentFill ? { xs: 2, md: 4 } : 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minWidth: 0,
              flex: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              intent="secondary"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={submitting}
            >
              Quay lại
            </Button>
            {titleExtra ? (
              <>
                <Typography
                  variant="h6"
                  component="h1"
                  noWrap
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {title}
                </Typography>
                {titleExtra}
              </>
            ) : null}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {footerExtra}
            {onSubmit ? (
              <Button
                intent="primary"
                size="small"
                onClick={onSubmit}
                disabled={submitting || submitDisabled}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
              >
                {submitLabel}
              </Button>
            ) : null}
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 1,
              flexShrink: 0,
              mx: contentFill ? { xs: 2, md: 4 } : 0,
            }}
          >
            {error}
          </Alert>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: contentFill ? 'hidden' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            px: contentFill ? { xs: 2, md: 4 } : 0,
            pb: contentFill ? 0 : 2,
          }}
        >
          {contentPaper ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: radius.sm,
                border: 1,
                borderColor: 'divider',
                flex: '1 0 auto',
                ...(contentFill
                  ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
                  : {}),
              }}
            >
              {children}
            </Paper>
          ) : contentFill ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {children}
            </Box>
          ) : (
            children
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default FormPage;
